import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetCurrentUser,
  getGetCurrentUserQueryKey,
  setActingTenantProvider,
  setActingRoleProvider,
  type CurrentUser,
} from '@cdf/api-client-react';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/safe-storage';

/**
 * Staff session context.
 *
 * Extends the MSAL-based session with backend-resolved staff fields:
 * `staffRole` (effective — 'staff' | 'admin' | 'super_admin' | 'community' | null)
 * and `realStaffRole` (the actual staff_users.role, never affected by the
 * acting-role header).
 *
 * When the user has a real staff role, an acting role can be set to downgrade
 * to any role at or below the real level (including "community"). The acting
 * role is persisted to localStorage and sent via the `X-Acting-Role` header on
 * every API request (registered through the mutator). Switching invalidates
 * all queries so pages immediately reflect reduced permissions.
 *
 * The acting-tenant (`X-Acting-Tenant-Id`) override only applies when the
 * **effective** role is `super_admin`.
 */

export type StaffRole = 'staff' | 'admin' | 'super_admin';

/** Roles the user can act as — includes "community" for the reporter view. */
export type ActingRole = StaffRole | 'community';

/** Display labels for the role switcher. */
export const ROLE_LABELS: Record<ActingRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  staff: 'Staff',
  community: 'Community member',
};

/** Rank order — higher rank = more permissions. */
const ROLE_RANK: Record<ActingRole, number> = {
  community: 0,
  staff: 1,
  admin: 2,
  super_admin: 3,
};

const ACTING_TENANT_KEY = 'cdf-acting-tenant-id';
const ACTING_ROLE_KEY = 'cdf-acting-role';

/**
 * Clamp the acting role: only allow roles at or below the real role.
 * Returns null if the acting role is invalid or would escalate.
 */
function clampActingRole(
  acting: string | null,
  realRole: StaffRole | null
): ActingRole | null {
  if (!acting || !realRole) return null;
  const validRoles: ActingRole[] = ['community', 'staff', 'admin', 'super_admin'];
  if (!validRoles.includes(acting as ActingRole)) return null;
  const role = acting as ActingRole;
  if (ROLE_RANK[role] > ROLE_RANK[realRole]) return null;
  // If acting role equals real role, no downgrade is needed.
  if (role === realRole) return null;
  return role;
}

interface StaffSessionValue {
  /** The signed-in user's profile from /api/auth/me (null while loading or signed out). */
  profile: CurrentUser | null;
  /** True while the profile query is loading. */
  loading: boolean;
  /** Effective staff role (reflects acting-role downgrade), or null for non-staff. */
  staffRole: StaffRole | 'community' | null;
  /** Real staff role from staff_users (never affected by acting-role header). */
  realStaffRole: StaffRole | null;
  /** Home tenant ID, or null if the user has no staff account. */
  homeTenantId: string | null;
  /** Currently acting tenant ID (null = home tenant). */
  actingTenantId: string | null;
  /** True when acting in a council other than home. */
  isActing: boolean;
  /** Switch to a council by tenant ID. Pass null to return to home. */
  setActingTenant: (tenantId: string | null) => void;
  /** Currently acting role (null = not downgraded, using real role). */
  actingRole: ActingRole | null;
  /** True when the acting role differs from the real role. */
  isActingRole: boolean;
  /** Switch to a lower role. Pass null to return to the real role. */
  setActingRole: (role: ActingRole | null) => void;
}

const StaffSessionContext = React.createContext<StaffSessionValue | null>(null);

export function StaffSessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  // Fetch the user profile (includes staffRole + realStaffRole + homeTenantId from the backend).
  const { data: profile, isLoading } = useGetCurrentUser({
    query: { queryKey: getGetCurrentUserQueryKey(), staleTime: 60_000, retry: 1 },
  });

  const realStaffRole = (profile?.realStaffRole ?? null) as StaffRole | null;
  const homeTenantId = profile?.homeTenantId ?? null;

  // Acting tenant state — persisted to localStorage.
  const [actingTenantId, setActingTenantIdState] = React.useState<string | null>(() => {
    return safeGetItem(ACTING_TENANT_KEY) || null;
  });

  // Acting role state — persisted to localStorage.
  const [actingRole, setActingRoleState] = React.useState<ActingRole | null>(() => {
    return (safeGetItem(ACTING_ROLE_KEY) as ActingRole | null) || null;
  });

  // Apply acting-role changes synchronously so route guards and navigation
  // reflect a role reset before the next profile query completes.
  const effectiveActingRole = clampActingRole(actingRole, realStaffRole);
  const staffRole = effectiveActingRole ?? realStaffRole ??
    ((profile?.staffRole ?? null) as StaffRole | 'community' | null);

  // Client-side clamp: clear any stored acting role above the real role
  // or invalid values. Also clear the acting tenant when the effective role
  // is not super_admin.
  React.useEffect(() => {
    const clamped = clampActingRole(
      safeGetItem(ACTING_ROLE_KEY),
      realStaffRole
    );
    if (clamped !== actingRole) {
      if (clamped) {
        safeSetItem(ACTING_ROLE_KEY, clamped);
      } else {
        safeRemoveItem(ACTING_ROLE_KEY);
      }
      setActingRoleState(clamped);
    }

    // Only super_admin (effective) should use the acting-tenant header.
    if (staffRole !== 'super_admin' && actingTenantId) {
      safeRemoveItem(ACTING_TENANT_KEY);
      setActingTenantIdState(null);
    }
  }, [realStaffRole, staffRole, actingRole, actingTenantId]);

  // Register the acting-tenant provider with the mutator so every API call
  // includes the X-Acting-Tenant-Id header when a super_admin has switched.
  React.useEffect(() => {
    setActingTenantProvider(() => actingTenantId);
    return () => setActingTenantProvider(null);
  }, [actingTenantId]);

  // Register the acting-role provider with the mutator so every API call
  // includes the X-Acting-Role header when downgraded.
  React.useEffect(() => {
    setActingRoleProvider(() => effectiveActingRole);
    return () => setActingRoleProvider(null);
  }, [effectiveActingRole]);

  const setActingTenant = React.useCallback(
    (tenantId: string | null) => {
      setActingTenantIdState((prev) => {
        const next = tenantId ?? null;
        if (next === prev) return prev;

        if (next) {
          safeSetItem(ACTING_TENANT_KEY, next);
        } else {
          safeRemoveItem(ACTING_TENANT_KEY);
        }

        // Invalidate all workspace queries so they refetch with the new tenant scope.
        queryClient.invalidateQueries();

        return next;
      });
    },
    [queryClient]
  );

  const setActingRole = React.useCallback(
    (role: ActingRole | null) => {
      setActingRoleState((prev) => {
        // Clamp: only allow roles at or below the real role.
        const next = role && realStaffRole && ROLE_RANK[role] <= ROLE_RANK[realStaffRole] && role !== realStaffRole
          ? role
          : null;

        if (next === prev) return prev;

        if (next) {
          safeSetItem(ACTING_ROLE_KEY, next);
        } else {
          safeRemoveItem(ACTING_ROLE_KEY);
        }

        // Invalidate all queries so they refetch with the new role scope.
        queryClient.invalidateQueries();

        return next;
      });
    },
    [queryClient, realStaffRole]
  );

  const value = React.useMemo<StaffSessionValue>(
    () => ({
      profile: profile ?? null,
      loading: isLoading,
      staffRole,
      realStaffRole,
      homeTenantId,
      actingTenantId,
      isActing: !!actingTenantId && actingTenantId !== homeTenantId,
      setActingTenant,
      actingRole: effectiveActingRole,
      isActingRole: !!effectiveActingRole,
      setActingRole,
    }),
    [
      profile,
      isLoading,
      staffRole,
      realStaffRole,
      homeTenantId,
      actingTenantId,
      setActingTenant,
      effectiveActingRole,
      setActingRole,
    ]
  );

  return <StaffSessionContext.Provider value={value}>{children}</StaffSessionContext.Provider>;
}

export function useStaffSession(): StaffSessionValue {
  const ctx = React.useContext(StaffSessionContext);
  if (!ctx) {
    throw new Error('useStaffSession must be used within StaffSessionProvider');
  }
  return ctx;
}
